import { ApolloClient, OperationVariables } from "@apollo/client";

import { ApolloMutationHook } from "types/Mutation";

/**
 * @propagateError - if true, the error will be propagated to the caller, mostly used in the bulk actions
 */
type HandleMutationProps<FetchResultData, MutationVariables extends OperationVariables> = {
  mutation: ApolloMutationHook<FetchResultData, MutationVariables>;
  variables: MutationVariables;
  mutationCallback?: (data: ApolloClient.MutateResult<FetchResultData>["data"]) => void;
} & (
  | {
      propagateError?: boolean;
      errorCallback: (error: unknown) => void;
    }
  | { propagateError: true; errorCallback?: never }
);

export const handleMutation = async <
  FetchResultData,
  MutationVariables extends OperationVariables,
>({
  mutation,
  variables,
  mutationCallback,
  propagateError,
  errorCallback,
}: HandleMutationProps<
  FetchResultData,
  MutationVariables
>): Promise<ApolloClient.MutateResult<FetchResultData> | void> => {
  try {
    const response = await mutation({ variables });

    mutationCallback?.(response.data);

    return response;
  } catch (error) {
    if (propagateError) {
      // propagate error to the caller
      throw error;
    } else {
      errorCallback?.(error);
    }
  }
};
